import type { Middleware, Action } from '@reduxjs/toolkit';
import { RootState } from '.';
import { Sale } from '@src/core/buyerApiClient';
import { setBuyer, clearBuyer } from '@src/features/customers';
import { setPublicKey } from '@src/stores/app';
import { receiveAvailableDates } from '@src/stores/product';
import { setCartModalOpenState } from '@src/features/cart/store/cart';
import {
  setProcessedSale,
  triggerExternalAuthError,
  triggerExternalAuthStatus,
  setSaleRequiredFormInputsState,
} from '@src/stores/sale';
import { setPaymentErrors } from '@src/features/payments/store/payment';
import { track, identify, reset } from '@src/services/analytics';

const monitoringMiddleware: Middleware =
  ({ getState }) =>
  (next) =>
  (action) => {
    const state = getState();
    const result = next(action);

    handleAction(action, state);

    return result;
  };

const handleAction = (action: Action, state: RootState) => {
  if (setPublicKey.match(action)) {
    track('widgetLoaded', {
      publicKey: action.payload,
    });
  }

  if (receiveAvailableDates.match(action)) {
    track('productAdderInitiated', {
      productId: action.meta.productId,
      indexKey: action.meta.key,
      hasDates: action.payload.length > 0,
    });
  }

  if (setCartModalOpenState.match(action)) {
    track('cartPreviewToggle', {
      productQuantity: state.cart.cart?.items.length ?? 0,
    });
  }

  if (setBuyer.match(action)) {
    identify(action.payload.id.toString(), {
      name: action.payload.fullName,
      isNewAccount: action.meta.isNewAccount,
    });
  }

  if (clearBuyer.match(action)) {
    reset();
  }

  if (setSaleRequiredFormInputsState.match(action)) {
    track('checkoutBuyerInfoCollectSubmit', {
      saleId: action.meta.sale.id,
      state: action.payload,
    });
  }

  if (setProcessedSale.match(action)) {
    track('checkoutPaymentSuccess', mountSaleTrackTraits(action.payload));
  }

  if (setPaymentErrors.match(action)) {
    track('checkoutPaymentFail', { errors: action.payload });
  }

  if (triggerExternalAuthError.match(action)) {
    track('checkoutExternalAuthenticationError');
  }

  if (triggerExternalAuthStatus.match(action)) {
    track('checkoutExternalAuthenticationStatus', {
      authStatus: action.payload,
    });
  }
};

const mountSaleTrackTraits = (sale: Sale) => ({
  saleId: sale.id,
  amountCents: sale.totalAmountCents,
  paymentMethodCode: sale.lastTransaction?.paymentMethod.code,
});

export default monitoringMiddleware;
